import { SortIcon } from '../atoms';
import type { Column, SortState } from '../../types/table';

/**
 * TableHeader Component - Molecule Level
 * 
 * Renders table header with sorting capabilities
 * 
 * Features:
 * - Sortable columns
 * - Sort indicators
 * - Responsive column widths
 * - Accessible (keyboard navigation, ARIA)
 * - Sticky header support
 */

export interface TableHeaderProps<TData = any> {
  /** Column definitions */
  columns: Column<TData>[];
  /** Current sort state */
  sortState: SortState | null;
  /** Callback when sort changes */
  onSortChange?: (columnId: string) => void;
  /** Whether row selection is enabled */
  hasSelection?: boolean;
  /** Whether all rows are selected */
  allSelected?: boolean;
  /** Callback when select all is toggled */
  onSelectAll?: () => void;
  /** Whether actions column is present */
  hasActions?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const TableHeader = <TData extends Record<string, any>>({
  columns,
  sortState,
  onSortChange,
  hasSelection = false,
  allSelected = false,
  onSelectAll,
  hasActions = false,
  className = '',
}: TableHeaderProps<TData>) => {
  /**
   * Handle column header click for sorting
   */
  const handleHeaderClick = (column: Column<TData>) => {
    if (column.sortable && onSortChange) {
      onSortChange(column.id);
    }
  };

  /**
   * Get current sort direction for a column
   */
  const getSortDirection = (columnId: string) => {
    if (sortState?.columnId === columnId) {
      return sortState.direction;
    }
    return null;
  };

  return (
    <thead className={`bg-gray-50 border-b border-gray-200 ${className}`}>
      <tr>
        {/* Selection Checkbox */}
        {hasSelection && (
          <th className="px-6 py-3 w-12">
            <div className="flex items-center justify-center">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onSelectAll}
                className="w-4 h-4 text-[#EAB308] bg-white border-gray-300 rounded focus:ring-[#EAB308] focus:ring-2 cursor-pointer"
                aria-label="Select all rows"
              />
            </div>
          </th>
        )}

        {/* Column Headers */}
        {columns.map((column) => {
          const sortDirection = getSortDirection(column.id);
          const isSortable = column.sortable;
          const headerContent = typeof column.header === 'string' ? column.header : column.header;

          return (
            <th
              key={column.id}
              className={`px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                column.hideOnMobile ? 'hidden md:table-cell' : ''
              } ${column.headerClassName || ''}`}
              style={{
                width: column.width,
                minWidth: column.minWidth,
              }}
            >
              <div
                className={`flex items-center gap-1 ${
                  isSortable ? 'cursor-pointer select-none hover:text-[#EAB308] transition-colors' : ''
                }`}
                onClick={() => isSortable && handleHeaderClick(column)}
                onKeyDown={(e) => {
                  if (isSortable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleHeaderClick(column);
                  }
                }}
                role={isSortable ? 'button' : undefined}
                tabIndex={isSortable ? 0 : undefined}
                aria-sort={
                  sortDirection === 'asc'
                    ? 'ascending'
                    : sortDirection === 'desc'
                    ? 'descending'
                    : undefined
                }
              >
                <span>{headerContent}</span>
                {isSortable && <SortIcon direction={sortDirection} />}
              </div>
            </th>
          );
        })}

        {/* Actions Column */}
        {hasActions && (
          <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
            Actions
          </th>
        )}
      </tr>
    </thead>
  );
};

export default TableHeader;

